import React, { useMemo, useState } from 'react';
import { Customer } from '../../types';
import { MergeCarryoverError, mergeCustomers } from '../../services/crmService';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import { AlertTriangle, Building2, GitMerge, Loader2, Search, X } from 'lucide-react';

/**
 * Kunden zusammenfuehren.
 *
 * Die Warnbox ist der Kern des Dialogs: Kundennummern lassen sich technisch
 * nicht zusammenlegen, und ausgestellte Rechnungen sind festgeschrieben —
 * beides steht hier woertlich, BEVOR etwas passiert, statt hinterher als
 * Datenbankfehler.
 */
interface Props {
    source: Customer;
    /** Auswahlliste fuer das Ziel — aktive Kunden, die Quelle selbst wird gefiltert. */
    customers: Customer[];
    onClose: () => void;
    /** Nach erfolgreichem Merge, mit dem Zielkunden. */
    onMerged: (target: Customer) => void;
}

export const MergeCustomersDialog: React.FC<Props> = ({ source, customers, onClose, onMerged }) => {
    const [query, setQuery] = useState('');
    const [target, setTarget] = useState<Customer | null>(null);
    const [understood, setUnderstood] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Merge durch, Stammdaten-Nachzug nicht — kein Fehlschlag, aber Hinweis. */
    const [carryWarning, setCarryWarning] = useState<string | null>(null);

    const eligible = useMemo(
        () =>
            customers.filter(
                (c) =>
                    c.id !== source.id &&
                    !c.archivedAt &&
                    (!query.trim() ||
                        c.name.toLowerCase().includes(query.trim().toLowerCase()) ||
                        (c.customerNumber || '').includes(query.trim())),
            ),
        [customers, source.id, query],
    );

    const submit = async () => {
        if (!target) return;
        setBusy(true);
        setError(null);
        setCarryWarning(null);
        try {
            await mergeCustomers(source.id, target.id);
            onMerged(target);
        } catch (e) {
            // Der Nachzug der Stammdaten ist gescheitert, der Merge selbst nicht:
            // hier darf NICHT zum Wiederholen aufgefordert werden — die
            // Referenzen sind bereits umgehaengt. Warnung zeigen, Vorgang als
            // erledigt abschliessen lassen.
            if (e instanceof MergeCarryoverError) setCarryWarning(e.message);
            else setError(rechtFehlerOder(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <GitMerge className="w-5 h-5 text-blue-400" /> Kunden zusammenführen
                    </h3>
                    {/* Nach durchgelaufenem Merge ist Schliessen kein Abbruch mehr:
                        die Liste muss den neuen Stand sehen. */}
                    <button
                        onClick={() => (carryWarning && target ? onMerged(target) : onClose())}
                        disabled={busy}
                        className="hover:bg-white/10 p-1 rounded"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <div className="text-sm text-slate-700">
                        Quelle: <strong>{source.name}</strong>
                        {source.customerNumber && <span className="font-mono text-slate-400"> ({source.customerNumber})</span>}
                    </div>

                    {/* Zielauswahl */}
                    <div>
                        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Ziel-Kunde</label>
                        <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Nach Name oder Kundennummer suchen…"
                                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                        </div>
                        <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-100 rounded-xl p-1">
                            {eligible.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setTarget(c)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                                        target?.id === c.id ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'
                                    }`}
                                >
                                    <Building2 className="w-4 h-4 shrink-0 opacity-60" />
                                    <span className="truncate font-medium">{c.name}</span>
                                    {c.customerNumber && (
                                        <span className={`font-mono text-[11px] shrink-0 ${target?.id === c.id ? 'text-blue-200' : 'text-slate-400'}`}>
                                            {c.customerNumber}
                                        </span>
                                    )}
                                </button>
                            ))}
                            {eligible.length === 0 && (
                                <p className="px-3 py-4 text-center text-xs text-slate-400">Kein passender Zielkunde.</p>
                            )}
                        </div>
                    </div>

                    {/* Die klare Meldung — fest sichtbar, nicht wegklickbar. */}
                    {target && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
                            <div className="flex items-center gap-2 font-bold">
                                <AlertTriangle className="w-4 h-4 shrink-0" /> Was passiert:
                            </div>
                            <ul className="list-disc ml-5 space-y-1">
                                <li>Projekte, Angebote, Kundenpreise und Guthaben wandern zu <strong>{target.name}</strong>.</li>
                                <li>
                                    Ansprechpartner und Rahmenverträge von <strong>{source.name}</strong> werden
                                    bei <strong>{target.name}</strong> ergänzt, soweit dort noch nicht vorhanden.
                                </li>
                                <li>
                                    Felder, die bei <strong>{target.name}</strong> <strong>leer</strong> sind
                                    (E-Mail, Telefon, Website, Anrede, Titel, Notizen), werden aus{' '}
                                    <strong>{source.name}</strong> aufgefüllt.
                                </li>
                                <li><strong>{source.name}</strong> wird archiviert.</li>
                            </ul>
                            <div className="font-bold pt-1">Was NICHT passiert:</div>
                            <ul className="list-disc ml-5 space-y-1">
                                <li>
                                    Kundennummern werden <strong>nicht zusammengelegt</strong> — das ist technisch
                                    nicht möglich. Die Nummer{source.customerNumber ? <> <strong>{source.customerNumber}</strong></> : null} bleibt
                                    dauerhaft am archivierten Kunden.
                                </li>
                                <li>
                                    Ausgestellte Rechnungen bleiben unverändert am alten Kunden (GoBD). Sie
                                    erscheinen weiterhin in dessen Akte und werden dem Umsatz von <strong>{target.name}</strong> zugerechnet.
                                </li>
                                <li>
                                    Ausgefüllte Felder von <strong>{target.name}</strong> werden <strong>nicht</strong> überschrieben:
                                    dessen Bewertungen, Konditionen und Notizen bleiben wie sie sind. Notizen
                                    beider Kunden werden nicht zusammengefügt.
                                </li>
                            </ul>
                            <label className="flex items-start gap-2 pt-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={understood}
                                    onChange={(e) => setUnderstood(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 rounded border-amber-300 text-amber-600"
                                />
                                <span>Ich habe verstanden, dass Kundennummer und Rechnungen beim alten Kunden bleiben.</span>
                            </label>
                        </div>
                    )}

                    {carryWarning && (
                        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{carryWarning}</span>
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-red-600 flex items-start gap-1.5">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{error}
                        </p>
                    )}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
                    {carryWarning && target ? (
                        // Zusammengefuehrt ist zusammengefuehrt: nur noch weiter.
                        <Button variant="primary" onClick={() => onMerged(target)}>
                            Verstanden — weiter
                        </Button>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
                            <Button
                                variant="primary"
                                onClick={() => void submit()}
                                disabled={!target || !understood || busy}
                                icon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
                            >
                                Zusammenführen
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
